import {
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { Typography } from '@material-tailwind/react';
import {
  LAST_LESSON_STEP,
  LESSON_SCENE_MAP,
  LESSON_STEPS,
  ReadRepeatPhase,
  SentenceMeaningItem,
  StartScene,
  STORYTELLING_HINT_POINTS,
} from '../../constants/types/free-trial/type';

interface FreeTrialLessonGuideProps {
  isReporting: boolean;
  currentStep: number;
  sceneIndex: number;
  audioProgress: number;
  isAudioPlaying: boolean;
  isAudioProgressCompleted: boolean;
  activityProgress: number;
  isActivityCompleted: boolean;
  meaningStep: number;
  pointRepeatStep: number;
  pointRepeatPartIndex: number;
  pointRepeatIndex: number;
  readRepeatPhase: ReadRepeatPhase;
  isStoryHintVisible: boolean;
  storyHintIndex: number;
  isSceneStarted: boolean;
  isPointRepeatPhaseStarted: boolean;
  onPrev: () => void;
  onNext: () => void;
  onReplay: () => void;
  onToggleStoryHint: () => void;
  onNextStoryHint: () => void;
  onRestartStoryHint: () => void;
}

const FreeTrialLessonGuide = ({
  isReporting,
  currentStep,
  sceneIndex,
  audioProgress,
  isAudioPlaying,
  isAudioProgressCompleted,
  activityProgress,
  isActivityCompleted,
  meaningStep,
  pointRepeatStep,
  pointRepeatPartIndex,
  pointRepeatIndex,
  readRepeatPhase,
  isStoryHintVisible,
  storyHintIndex,
  isSceneStarted,
  isPointRepeatPhaseStarted,
  onPrev,
  onNext,
  onReplay,
  onToggleStoryHint,
  onNextStoryHint,
  onRestartStoryHint,
}: FreeTrialLessonGuideProps) => {
  const scenes = LESSON_SCENE_MAP[currentStep] ?? [];
  const scene = scenes[sceneIndex];

  if (!scene) return null;

  if (scene.type === 'START') {
    return (
      <StartStepGuide
        scene={scene}
        audioProgress={audioProgress}
        isAudioPlaying={isAudioPlaying}
        onPrev={onPrev}
        onNext={onNext}
        onReplay={onReplay}
      />
    );
  }

  return (
    <LessonStepGuide
      isReporting={isReporting}
      currentStep={currentStep}
      sceneIndex={sceneIndex}
      audioProgress={audioProgress}
      isAudioProgressCompleted={isAudioProgressCompleted}
      activityProgress={activityProgress}
      isActivityCompleted={isActivityCompleted}
      meaningStep={meaningStep}
      pointRepeatStep={pointRepeatStep}
      pointRepeatPartIndex={pointRepeatPartIndex}
      pointRepeatIndex={pointRepeatIndex}
      readRepeatPhase={readRepeatPhase}
      isStoryHintVisible={isStoryHintVisible}
      storyHintIndex={storyHintIndex}
      isSceneStarted={isSceneStarted}
      isPointRepeatPhaseStarted={isPointRepeatPhaseStarted}
      onPrev={onPrev}
      onNext={onNext}
      onReplay={onReplay}
      onToggleStoryHint={onToggleStoryHint}
      onNextStoryHint={onNextStoryHint}
      onRestartStoryHint={onRestartStoryHint}
    />
  );
};

export default FreeTrialLessonGuide;

interface StartStepGuideProps {
  scene: StartScene;
  audioProgress: number;
  isAudioPlaying: boolean;
  onPrev: () => void;
  onNext: () => void;
  onReplay: () => void;
}

const StartStepGuide = ({
  scene,
  audioProgress,
  isAudioPlaying,
  onPrev,
  onNext,
  onReplay,
}: StartStepGuideProps) => {
  const isReady = scene.mode === 'READY';
  const isBookIntro = scene.mode === 'BOOK_INTRO';
  const isStoryIntro = scene.mode === 'STORY_INTRO';

  return (
    <div className="flex flex-col rounded-2xl border border-[#DDD5F5] bg-white p-3 md:p-4">
      <Typography className="text-[10px] font-extrabold text-[#6737F4]">
        시작
      </Typography>

      <Typography className="mt-3 hidden text-[20px] font-black text-gray-900 md:block">
        {scene.guideTitle}
      </Typography>

      <div className="mt-2 rounded-xl bg-[#EEE9FF] px-3 py-2 md:mt-4 md:px-4 md:py-2.5">
        <span className="inline-flex rounded-full bg-[#6737F4] px-2.5 py-1 text-[9px] font-bold text-white">
          엄마 가이드
        </span>

        <Typography className="mt-2 text-[12px] font-extrabold leading-5 text-gray-900 md:text-[13px]">
          {scene.guideMessage}
        </Typography>

        <Typography className="mt-3 hidden text-[11px] leading-5 text-gray-700 md:block">
          실제 수업에서 엄마·선생님이 안내하는 말을 ‘엄마 가이드’로 담았어요.
        </Typography>
      </div>

      <Typography className="mt-2 text-[10px] leading-4 text-gray-700 md:mt-4 md:text-[11px] md:leading-5">
        {scene.guideTip}
      </Typography>

      {isBookIntro && isAudioPlaying && (
        <BookIntroProgress progress={audioProgress} />
      )}

      {isStoryIntro ? (
        <div className="mt-3 grid grid-cols-[44px_minmax(0,1fr)] gap-2 md:mt-4">
          <button
            type="button"
            onClick={onPrev}
            className="flex h-10 items-center justify-center rounded-xl border border-[#DDD5F5] text-[#6737F4] transition hover:bg-[#F7F4FF]"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>

          <button
            type="button"
            onClick={onNext}
            className="flex h-10 items-center justify-center gap-1.5 rounded-xl bg-[#6737F4] text-[11px] font-bold text-white transition hover:bg-[#5425D6]"
          >
            다음
            <ChevronRightIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-[44px_minmax(0,1fr)_minmax(0,1.5fr)] gap-2 md:mt-4">
          <button
            type="button"
            onClick={onPrev}
            disabled={isReady}
            className={`flex h-10 items-center justify-center rounded-xl border transition ${
              isReady
                ? 'cursor-default border-gray-200 bg-gray-50 text-gray-300'
                : 'border-[#DDD5F5] text-[#6737F4] hover:bg-[#F7F4FF]'
            }`}
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>

          <button
            type="button"
            onClick={onReplay}
            disabled={isReady}
            className={`flex h-10 items-center justify-center gap-1.5 rounded-xl border text-[11px] font-bold transition ${
              isReady
                ? 'cursor-default border-gray-200 bg-gray-50 text-gray-300'
                : 'border-[#DDD5F5] text-[#6737F4] hover:bg-[#F7F4FF]'
            }`}
          >
            <ArrowPathIcon className="h-3.5 w-3.5" />
            다시
          </button>

          <button
            type="button"
            onClick={onNext}
            disabled={isReady}
            className={`flex h-10 items-center justify-center gap-1.5 rounded-xl text-[11px] font-bold transition ${
              isReady
                ? 'cursor-default bg-[#E4DEFA] text-white'
                : 'bg-[#6737F4] text-white hover:bg-[#5425D6]'
            }`}
          >
            다음
            <ChevronRightIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};

interface LessonStepGuideProps {
  isReporting: boolean;
  currentStep: number;
  sceneIndex: number;
  audioProgress: number;
  isAudioProgressCompleted: boolean;
  activityProgress: number;
  isActivityCompleted: boolean;
  meaningStep: number;
  pointRepeatStep: number;
  pointRepeatPartIndex: number;
  pointRepeatIndex: number;
  readRepeatPhase: ReadRepeatPhase;
  isStoryHintVisible: boolean;
  storyHintIndex: number;
  isSceneStarted: boolean;
  isPointRepeatPhaseStarted: boolean;
  onPrev: () => void;
  onNext: () => void;
  onReplay: () => void;
  onToggleStoryHint: () => void;
  onNextStoryHint: () => void;
  onRestartStoryHint: () => void;
}

const LessonStepGuide = ({
  isReporting,
  currentStep,
  sceneIndex,
  audioProgress,
  isAudioProgressCompleted,
  activityProgress,
  isActivityCompleted,
  meaningStep,
  pointRepeatStep,
  pointRepeatPartIndex,
  pointRepeatIndex,
  readRepeatPhase,
  isStoryHintVisible,
  storyHintIndex,
  isSceneStarted,
  isPointRepeatPhaseStarted,
  onPrev,
  onNext,
  onReplay,
  onToggleStoryHint,
  onNextStoryHint,
  onRestartStoryHint,
}: LessonStepGuideProps) => {
  const scenes = LESSON_SCENE_MAP[currentStep] ?? [];
  const scene = scenes[sceneIndex];

  if (!scene) return null;

  const isTalk = scene.type === 'SENTENCE' && scene.activity === 'TALK';

  const isListen = scene.type === 'SENTENCE' && scene.activity === 'LISTEN';

  const isReadRepeat =
    scene.type === 'SENTENCE' && scene.activity === 'READ_REPEAT';

  const isMeaning = scene.type === 'SENTENCE' && scene.activity === 'MEANING';

  const meaningGroups =
    isMeaning && scene.type === 'SENTENCE'
      ? (scene.meaningGroups ??
        (scene.meaningItems?.length ? [scene.meaningItems] : []))
      : [];

  const totalMeaningChunks = meaningGroups.flat().length;

  const meaningProgress = (() => {
    if (!isMeaning || meaningGroups.length === 0) {
      return {
        groupIndex: 0,
        chunkIndex: 0,
        chunkCount: 0,
      };
    }

    const currentStep = Math.min(Math.max(meaningStep, 1), totalMeaningChunks);

    let accumulated = 0;

    for (
      let groupIndex = 0;
      groupIndex < meaningGroups.length;
      groupIndex += 1
    ) {
      const group = meaningGroups[groupIndex];

      if (currentStep <= accumulated + group.length) {
        return {
          groupIndex,
          chunkIndex: currentStep - accumulated - 1,
          chunkCount: group.length,
        };
      }

      accumulated += group.length;
    }

    const lastGroupIndex = meaningGroups.length - 1;
    const lastGroup = meaningGroups[lastGroupIndex];

    return {
      groupIndex: lastGroupIndex,
      chunkIndex: lastGroup.length - 1,
      chunkCount: lastGroup.length,
    };
  })();

  const currentMeaningGroup = meaningGroups[meaningProgress.groupIndex] ?? [];

  const isMeaningCompleted =
    totalMeaningChunks > 0 && meaningStep > totalMeaningChunks;

  const visibleMeaningGuideItems = isMeaningCompleted
    ? meaningGroups.flat()
    : currentMeaningGroup;

  const isListenCompleted = isListen && isAudioProgressCompleted;

  const isReadRepeatRepeating = isReadRepeat && readRepeatPhase === 'REPEAT';

  const talkDuration =
    scene.type === 'SENTENCE' ? (scene.durationSeconds ?? 15) : 15;

  const remainingSeconds = Math.max(
    0,
    Math.ceil(talkDuration * (1 - activityProgress / 100)),
  );

  const isListenRepeat =
    scene.type === 'SENTENCE' && scene.activity === 'LISTEN_REPEAT';

  const isListenRepeatRepeating =
    isListenRepeat && isAudioProgressCompleted && !isActivityCompleted;

  const isPointRepeat =
    scene.type === 'SENTENCE' && scene.activity === 'POINT_REPEAT';

  const pointRepeatIsRepeatPhase = pointRepeatStep >= 4;

  const guideTitle =
    isPointRepeat && pointRepeatIsRepeatPhase
      ? '그림 짚으며 따라 해요'
      : scene.guideTitle;

  const guideMessage = isPointRepeat
    ? pointRepeatIsRepeatPhase
      ? '“자, 이제 손가락 표시를 따라 그림을 짚어 가며 따라 하세요.”'
      : pointRepeatStep === 2
        ? '“이제 손가락을 보면서 이어서 들어 봐요.”'
        : pointRepeatStep === 3
          ? '“아이가 직접 짚으며 따라 할 차례예요.”'
          : scene.guideMessage
    : scene.guideMessage;

  const guideTip =
    isPointRepeat && pointRepeatIsRepeatPhase
      ? '아이가 화면이나 교재 그림을 직접 손가락으로 짚으며 말하게 해 주세요.'
      : scene.guideTip;

  const pointRepeatParts =
    isPointRepeat && scene.type === 'SENTENCE'
      ? (scene.pointRepeatParts ?? [])
      : [];

  const currentPointRepeatPart = pointRepeatParts[pointRepeatPartIndex];

  const currentPointRepeatPoint =
    currentPointRepeatPart?.points[pointRepeatIndex];

  const isLastSentence = currentStep === 3;

  const isSectionIntro = scene.type === 'SECTION_INTRO';

  const isWatchStory = scene.type === 'STORY' && scene.mode === 'WATCH';

  const isStorytelling =
    scene.type === 'STORY' && scene.mode === 'STORYTELLING';

  const storytellingDuration =
    isStorytelling && scene.type === 'STORY'
      ? (scene.durationSeconds ?? 20)
      : 20;

  const storytellingRemainingSeconds = Math.max(
    0,
    Math.ceil(storytellingDuration * (1 - activityProgress / 100)),
  );

  const storyHintTotal = STORYTELLING_HINT_POINTS.length;

  const isLastStoryHint = storyHintIndex === storyHintTotal - 1;

  return (
    <div className="flex flex-col rounded-2xl border border-[#DDD5F5] bg-white p-3 md:p-4">
      <div className="flex items-center justify-between gap-2">
        <Typography className="text-[10px] font-extrabold text-[#6737F4]">
          {scene.type === 'SENTENCE' && scene.stage && scene.stageTitle
            ? `${scene.stage}단계 · ${scene.stageTitle}`
            : LESSON_STEPS[currentStep]}
        </Typography>

        {scene.type === 'SENTENCE' && (
          <Typography className="text-[10px] font-bold text-gray-700">
            문장 {currentStep} / 3
          </Typography>
        )}
      </div>

      {scene.type === 'SENTENCE' && scenes.length > 1 && (
        <div
          className="mt-2 grid gap-1.5 md:mt-3"
          style={{
            gridTemplateColumns: `repeat(${scenes.length}, minmax(0, 1fr))`,
          }}
        >
          {scenes.map((item, index) => {
            const isCurrent = index === sceneIndex;
            const isPast = index < sceneIndex;

            return (
              <span
                key={item.key}
                className={`h-1.5 rounded-full ${
                  isCurrent
                    ? 'bg-[#6737F4]'
                    : isPast
                      ? 'bg-[#A996F6]'
                      : 'bg-[#E6E1F5]'
                }`}
              />
            );
          })}
        </div>
      )}

      <Typography className="mt-3 hidden text-[20px] font-black text-gray-900 md:block">
        {guideTitle}
      </Typography>

      <div className="mt-2 rounded-xl bg-[#EEE9FF] px-3 py-2 md:mt-4 md:px-4 md:py-2.5">
        <span className="inline-flex rounded-full bg-[#6737F4] px-2.5 py-1 text-[9px] font-bold text-white">
          엄마 가이드
        </span>

        <Typography className="mt-2 text-[12px] font-extrabold leading-5 text-gray-900 md:text-[13px]">
          {isMeaning &&
          scene.type === 'SENTENCE' &&
          currentMeaningGroup.length > 0 ? (
            <MeaningGuideMessage items={visibleMeaningGuideItems} />
          ) : isPointRepeat &&
            pointRepeatStep <= 1 &&
            currentPointRepeatPoint ? (
            <PointRepeatGuideMessage
              message={currentPointRepeatPoint.guideMessage}
              englishText={currentPointRepeatPoint.text}
            />
          ) : (
            guideMessage
          )}
        </Typography>

        <Typography className="mt-3 hidden border-t border-dashed border-[#CFC4FF] pt-2 text-[11px] leading-5 text-gray-700 md:block">
          실제 수업에서 엄마·선생님이 안내하는 말을 ‘엄마 가이드’로 담았어요.
        </Typography>
      </div>

      <Typography className="mt-2 text-[10px] leading-4 text-gray-700 md:mt-4 md:text-[11px] md:leading-5">
        {guideTip}
      </Typography>

      {isTalk && (
        <div className="mt-2 rounded-xl bg-[#EEE9FF] px-3 py-2 md:mt-4 md:py-2.5">
          <div className="flex items-center gap-2">
            <span
              className={`h-2 w-2 rounded-full ${
                isActivityCompleted
                  ? 'bg-gray-700'
                  : 'animate-pulse bg-[#F9C933] [animation-duration:1s]'
              }`}
            />

            <Typography className="text-[10px] font-bold text-gray-900 md:text-[11px]">
              {isActivityCompleted
                ? '다 이야기했으면 다음으로 넘어가요'
                : `아이가 이야기하는 시간 · ${remainingSeconds}초`}
            </Typography>
          </div>

          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#E4DEFA]">
            <div
              className={`h-full rounded-full ${
                activityProgress === 0
                  ? 'transition-none'
                  : 'transition-[width] duration-300 ease-linear'
              } ${isActivityCompleted ? 'bg-[#6737F4]' : 'bg-[#F9C933]'}`}
              style={{
                width: `${activityProgress}%`,
              }}
            />
          </div>
        </div>
      )}

      {isListen && isSceneStarted && (
        <div className="mt-2 rounded-xl bg-[#EEE9FF] px-3 py-2 md:mt-4 md:py-2.5">
          <div className="flex items-center gap-2">
            <span
              className={`h-2 w-2 rounded-full ${
                isListenCompleted
                  ? 'bg-gray-700'
                  : 'animate-pulse bg-[#6737F4] [animation-duration:1s]'
              }`}
            />

            <Typography className="text-[10px] font-bold text-gray-900 md:text-[11px]">
              {isListenCompleted
                ? '잘 들었어요. 다음으로 넘어가요'
                : '듣는 중...'}
            </Typography>
          </div>

          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#E4DEFA]">
            <div
              className={`h-full rounded-full ${
                audioProgress === 0
                  ? 'transition-none'
                  : 'transition-[width] duration-300 ease-linear'
              } bg-[#6737F4]`}
              style={{
                width: `${audioProgress}%`,
              }}
            />
          </div>
        </div>
      )}

      {isReadRepeat && isSceneStarted && (
        <div className="mt-2 rounded-xl bg-[#EEE9FF] px-3 py-2 md:mt-4 md:py-2.5">
          <div className="flex items-center gap-2">
            <span
              className={`h-2 w-2 rounded-full ${
                isActivityCompleted
                  ? 'bg-gray-700'
                  : isReadRepeatRepeating
                    ? 'animate-pulse bg-[#F9C933] [animation-duration:1s]'
                    : 'animate-pulse bg-[#6737F4] [animation-duration:1s]'
              }`}
            />

            <Typography className="text-[10px] font-bold text-gray-900 md:text-[11px]">
              {isActivityCompleted
                ? '소리를 듣고 따라 했어요'
                : readRepeatPhase === 'REPEAT'
                  ? '소리를 듣고 따라 해요'
                  : '듣는 중...'}
            </Typography>
          </div>

          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#E4DEFA]">
            <div
              className={`h-full rounded-full ${
                isReadRepeatRepeating
                  ? activityProgress === 0
                    ? 'bg-[#F9C933] transition-none'
                    : 'bg-[#F9C933] transition-[width] duration-300 ease-linear'
                  : audioProgress === 0
                    ? 'bg-[#6737F4] transition-none'
                    : 'bg-[#6737F4] transition-[width] duration-300 ease-linear'
              }`}
              style={{
                width: isActivityCompleted
                  ? '100%'
                  : isReadRepeatRepeating
                    ? `${activityProgress}%`
                    : `${audioProgress}%`,
              }}
            />
          </div>
        </div>
      )}

      {isMeaning && meaningStep > 0 && (
        <div className="mt-2 rounded-xl bg-[#EEE9FF] px-3 py-2 md:mt-4 md:py-2.5">
          <div className="flex items-center gap-2">
            <span
              className={`h-2 w-2 rounded-full ${
                isMeaningCompleted
                  ? 'bg-gray-700'
                  : 'animate-pulse bg-[#B9A7FF] [animation-duration:1s]'
              }`}
            />

            <Typography className="text-[10px] font-bold text-gray-900 md:text-[11px]">
              {isMeaningCompleted
                ? '뜻을 모두 알아봤어요'
                : `뜻 카드 ${
                    meaningProgress.chunkIndex + 1
                  } / ${meaningProgress.chunkCount} · ${
                    meaningProgress.groupIndex + 1
                  }부분`}
            </Typography>
          </div>

          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#E4DEFA]">
            <div
              className="h-full rounded-full bg-[#6737F4] transition-[width] duration-300 ease-linear"
              style={{
                width: isMeaningCompleted
                  ? '100%'
                  : `${
                      ((meaningProgress.chunkIndex + 1) /
                        meaningProgress.chunkCount) *
                      100
                    }%`,
              }}
            />
          </div>
        </div>
      )}

      {isListenRepeat && isSceneStarted && (
        <div className="mt-2 rounded-xl bg-[#EEE9FF] px-3 py-2 md:mt-4 md:py-2.5">
          <div className="flex items-center gap-2">
            <span
              className={`h-2 w-2 rounded-full ${
                isActivityCompleted
                  ? 'bg-gray-700'
                  : isListenRepeatRepeating
                    ? 'animate-pulse bg-[#F9C933] [animation-duration:1s]'
                    : 'animate-pulse bg-[#6737F4] [animation-duration:1s]'
              }`}
            />

            <Typography className="text-[10px] font-bold text-gray-900 md:text-[11px]">
              {isActivityCompleted
                ? '따라 했어요'
                : isListenRepeatRepeating
                  ? '아이가 따라 말하는 시간'
                  : '듣는 중...'}
            </Typography>
          </div>

          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#E4DEFA]">
            <div
              className={`h-full rounded-full ${
                isListenRepeatRepeating
                  ? activityProgress === 0
                    ? 'bg-[#F9C933] transition-none'
                    : 'bg-[#F9C933] transition-[width] duration-300 ease-linear'
                  : audioProgress === 0
                    ? 'bg-[#6737F4] transition-none'
                    : 'bg-[#6737F4] transition-[width] duration-300 ease-linear'
              }`}
              style={{
                width: isActivityCompleted
                  ? '100%'
                  : isListenRepeatRepeating
                    ? `${activityProgress}%`
                    : `${audioProgress}%`,
              }}
            />
          </div>
        </div>
      )}

      {isPointRepeat && isPointRepeatPhaseStarted && (
        <div className="mt-2 rounded-xl bg-[#EEE9FF] px-3 py-2 md:mt-4 md:py-2.5">
          <div className="flex items-center gap-2">
            <span
              className={`h-2 w-2 rounded-full ${
                pointRepeatStep === 1 || pointRepeatStep === 5
                  ? 'animate-pulse bg-[#F9B233] [animation-duration:1s]'
                  : pointRepeatStep === 3 || pointRepeatStep === 6
                    ? 'bg-gray-700'
                    : 'animate-pulse bg-[#6737F4] [animation-duration:1s]'
              }`}
            />

            <Typography className="text-[10px] font-bold text-gray-900 md:text-[11px]">
              {pointRepeatStep === 0
                ? `짚는 곳 ${pointRepeatIndex + 1} / ${
                    currentPointRepeatPart?.points.length ?? 0
                  }${
                    pointRepeatParts.length > 1
                      ? ` · ${pointRepeatPartIndex + 1}부분`
                      : ''
                  }`
                : pointRepeatStep === 1
                  ? '엄마가 설명해 주는 시간'
                  : pointRepeatStep === 2
                    ? '짚으며 듣는 중...'
                    : pointRepeatStep === 3
                      ? '아이가 직접 짚으며 따라 할 차례예요'
                      : pointRepeatStep === 4
                        ? '듣는 중...'
                        : pointRepeatStep === 5
                          ? '아이가 짚으며 따라 말하는 시간'
                          : isLastSentence
                            ? '세 그림을 모두 배웠어요'
                            : '다음 그림으로 넘어가요'}
            </Typography>
          </div>

          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#E4DEFA]">
            <div
              className={`h-full rounded-full ${
                pointRepeatStep === 1 || pointRepeatStep === 5
                  ? activityProgress === 0
                    ? 'bg-[#F9B233] transition-none'
                    : 'bg-[#F9B233] transition-[width] duration-300 ease-linear'
                  : audioProgress === 0
                    ? 'bg-[#6737F4] transition-none'
                    : 'bg-[#6737F4] transition-[width] duration-300 ease-linear'
              }`}
              style={{
                width:
                  pointRepeatStep === 3 || pointRepeatStep === 6
                    ? '100%'
                    : pointRepeatStep === 1 || pointRepeatStep === 5
                      ? `${activityProgress}%`
                      : `${audioProgress}%`,
              }}
            />
          </div>
        </div>
      )}

      {isWatchStory && isSceneStarted && (
        <div className="mt-2 rounded-xl bg-[#EEE9FF] px-3 py-2 md:mt-4 md:py-2.5">
          <div className="flex items-center gap-2">
            <span
              className={`h-2 w-2 rounded-full ${
                isAudioProgressCompleted
                  ? 'bg-gray-700'
                  : 'animate-pulse bg-[#6737F4] [animation-duration:1s]'
              }`}
            />

            <Typography className="text-[10px] font-bold text-gray-900 md:text-[11px]">
              {isAudioProgressCompleted
                ? '끝까지 들었어요'
                : '들으며 따라 하는 중…'}
            </Typography>
          </div>

          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#E4DEFA]">
            <div
              className={`h-full rounded-full bg-[#6737F4] ${
                audioProgress === 0
                  ? 'transition-none'
                  : 'transition-[width] duration-300 ease-linear'
              }`}
              style={{
                width: `${isAudioProgressCompleted ? 100 : audioProgress}%`,
              }}
            />
          </div>
        </div>
      )}

      {isStorytelling && (
        <div className="mt-2 rounded-xl bg-[#EEE9FF] px-3 py-2 md:mt-4 md:py-2.5">
          <div className="flex items-center gap-2">
            <span
              className={`h-2 w-2 rounded-full ${
                isActivityCompleted
                  ? 'bg-gray-700'
                  : 'animate-pulse bg-[#F9B233] [animation-duration:1s]'
              }`}
            />

            <Typography className="text-[10px] font-bold text-gray-900 md:text-[11px]">
              {isActivityCompleted
                ? '스스로 말하기를 마쳤어요'
                : `아이가 스스로 말하는 시간 · ${storytellingRemainingSeconds}초`}
            </Typography>
          </div>

          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#E4DEFA]">
            <div
              className={`h-full rounded-full ${
                activityProgress === 0
                  ? 'transition-none'
                  : 'transition-[width] duration-300 ease-linear'
              } ${isActivityCompleted ? 'bg-[#6737F4]' : 'bg-[#F9B233]'}`}
              style={{
                width: `${activityProgress}%`,
              }}
            />
          </div>
        </div>
      )}

      {isStorytelling && (
        <div className="mt-2 flex flex-wrap gap-2 md:mt-3">
          {!isStoryHintVisible ? (
            <button
              type="button"
              onClick={onToggleStoryHint}
              className="rounded-xl bg-[#FFD54A] px-4 py-2.5 text-[11px] font-extrabold text-gray-900 transition hover:bg-[#F6C933]"
            >
              힌트 보기
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onToggleStoryHint}
                className="rounded-xl border border-[#6737F4] bg-white px-4 py-2.5 text-[11px] font-extrabold text-[#6737F4] transition hover:bg-[#F7F4FF]"
              >
                힌트 숨기기
              </button>

              {!isLastStoryHint && (
                <button
                  type="button"
                  onClick={onNextStoryHint}
                  className="rounded-xl bg-[#FFD54A] px-4 py-2.5 text-[11px] font-extrabold text-gray-900 transition hover:bg-[#F6C933]"
                >
                  다음 손가락 →
                </button>
              )}

              {storyHintIndex > 0 && (
                <button
                  type="button"
                  onClick={onRestartStoryHint}
                  className="rounded-xl border border-[#6737F4] bg-white px-4 py-2.5 text-[11px] font-extrabold text-[#6737F4] transition hover:bg-[#F7F4FF]"
                >
                  처음부터
                </button>
              )}
            </>
          )}
        </div>
      )}

      <div
        className={`mt-3 grid gap-2 md:mt-4 ${
          isSectionIntro
            ? 'grid-cols-[44px_minmax(0,1fr)]'
            : 'grid-cols-[44px_minmax(0,1fr)_minmax(0,1.5fr)]'
        }`}
      >
        <button
          type="button"
          onClick={onPrev}
          className="flex h-10 items-center justify-center rounded-xl border border-[#DDD5F5] text-[#6737F4] transition hover:bg-[#F7F4FF]"
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </button>

        {!isSectionIntro && (
          <button
            type="button"
            onClick={onReplay}
            className="flex h-10 items-center justify-center gap-1.5 rounded-xl border border-[#DDD5F5] text-[11px] font-bold text-[#6737F4] transition hover:bg-[#F7F4FF]"
          >
            <ArrowPathIcon className="h-3.5 w-3.5" />
            다시
          </button>
        )}

        <button
          type="button"
          onClick={onNext}
          disabled={isReporting}
          className="flex h-10 items-center justify-center gap-1.5 rounded-xl bg-[#6737F4] text-[11px] font-bold text-white transition hover:bg-[#5425D6] disabled:bg-[#E4DEFA]"
        >
          다음
          <ChevronRightIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

interface BookIntroProgressProps {
  progress: number;
}

const BookIntroProgress = ({ progress }: BookIntroProgressProps) => {
  return (
    <div className="mt-2 rounded-xl bg-[#F5F2FF] px-3 py-2 md:mt-4 md:py-2.5">
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 animate-pulse rounded-full bg-[#6737F4] [animation-duration:1.2s]" />

        <Typography className="text-[10px] font-bold text-gray-900 md:text-[11px]">
          책 소개 듣는 중...
        </Typography>
      </div>

      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#E4DEFA]">
        <div
          className="h-full rounded-full bg-[#6737F4] transition-[width] duration-300 ease-linear"
          style={{
            width: `${progress}%`,
          }}
        />
      </div>
    </div>
  );
};

const MeaningGuideMessage = ({ items }: { items: SentenceMeaningItem[] }) => {
  return (
    <>
      “
      {items.map((item, index) => (
        <span key={item.text}>
          {index > 0 && ', '}
          <span className="text-red-500">{item.text}</span> {item.meaning}
        </span>
      ))}
      .”
    </>
  );
};

const PointRepeatGuideMessage = ({
  message,
  englishText,
}: {
  message: string;
  englishText: string;
}) => {
  const parts = message.split(englishText);

  return (
    <>
      {parts.map((part, index) => (
        <span key={index}>
          {index > 0 && <span className="text-red-500">{englishText}</span>}

          {part}
        </span>
      ))}
    </>
  );
};
